/**
 * Utilities for the Thread Audit modal: formatting helpers and print-via-iframe export.
 */

export function escapeHtml (str) {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function fmtMs (ms) {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export function shortHash (hash) {
  if (!hash) return ''
  return hash.substring(0, 8)
}

/**
 * Render HTML content in a hidden iframe and trigger the browser's print dialog.
 * Uses `afterprint` for reliable cleanup instead of fixed timeouts.
 *
 * @param {string} html  - HTML fragment to print
 * @param {string} title - Title used for the print filename and iframe document title
 * @param {Function} [onDone] - Callback invoked after the print dialog closes
 */
export function printViaIframe (html, title, onDone) {
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:1800px;height:900px;border:none;'
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow.document
  const styles = []
  for (const sheet of document.styleSheets) {
    try { for (const rule of sheet.cssRules) { styles.push(rule.cssText) } } catch { /* cross-origin */ }
  }

  const computed = getComputedStyle(document.documentElement)
  const vars = new Set()
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules) {
        const matches = rule.cssText.match(/--[\w-]+/g)
        if (matches) {
          matches.forEach(v => {
            const val = computed.getPropertyValue(v).trim()
            if (val) { vars.add(`${v}:${val}`) }
          })
        }
      }
    } catch { /* cross-origin */ }
  }

  doc.open()
  doc.write([
    '<!DOCTYPE html><html><head>',
    `<title>${escapeHtml(title)}</title>`,
    '<style>',
    `:root{${[...vars].join(';')}}`,
    styles.join('\n'),
    'body{margin:0;padding:20px;background:#fff;color:#1a1a1a}',
    '@media print{body{padding:8px}@page{margin:8mm;size:landscape}}',
    '</style></head><body>',
    html,
    '</body></html>'
  ].join(''))
  doc.close()

  const finish = () => {
    const originalTitle = document.title
    document.title = title

    let cleaned = false
    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      // Defer DOM teardown so it never runs while print() is still on the call stack
      // (Chrome fires afterprint synchronously inside the blocking print() call)
      setTimeout(() => {
        document.title = originalTitle
        if (iframe.parentNode) { document.body.removeChild(iframe) }
        if (onDone) { onDone() }
      }, 0)
    }

    iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true })

    iframe.contentWindow.focus()
    iframe.contentWindow.print()

    // Fallback: in case afterprint never fires
    setTimeout(cleanup, 5000)
  }

  if (doc.readyState === 'complete') { finish() } else { iframe.onload = finish }
}
